<template>
    <side-panel class="side-panel" id="freight-exchange-panel">
        <template #head>
            <div class="d-head">
                <div class="container-fluid">
                    <h3 class="panel-title">
                        {{ __('freight_exchange.title') }}
                    </h3>

                    <div @click="$emit('toggleUtilityPanel')" class="close-btn">
                        <i class="fa-solid fa-xmark"></i>
                    </div>
                </div>
            </div>
        </template>

        <template #body>
            <div v-if="freightExchangeData == 401">
                <golia-button variant="btn-fill golia" class="w-100 mt-3" style="font-size: 14px"
                    :label="__('freight_exchange.golia_exchange_sync')" @click="syncGoliaExchange" />
            </div>
            <div v-else>
                <b-tabs nav-wrapper-class="golia-classic-tabs my-2">
                    <!-- Tab Veicolo -->
                    <b-tab :active="getFEOffers.length === 0">
                        <template #title>
                            <span><i class="fa-solid fa-filter"></i> {{ __('freight_exchange.filters') }}</span>
                        </template>

                        <div class="mb-3">
                            <label for="vehicleType">
                                {{ __('freight_exchange.fields.vehicle_type') }}
                            </label>
                            <golia-multiselect v-model="vehicleType" :options="vehicleTypeOptions" :multiple="true"
                                :show-labels="false" track-by="id" label="name" :placeholder="__('select')"
                                :selectLabel="__('select')" :selectedLabel="__('selected')"
                                :deselectLabel="__('deselect')" />
                        </div>
                        <div class="mb-3">
                            <label for="vehicleSize">
                                {{ __('freight_exchange.fields.vehicle_size') }}
                            </label>
                            <golia-multiselect v-model="vehicleSize" :options="vehicleSizeOptions" :multiple="false"
                                :show-labels="false" track-by="id" label="name" :placeholder="__('select')"
                                :selectLabel="__('select')" :selectedLabel="__('selected')"
                                :deselectLabel="__('deselect')" />
                        </div>
                        <div class="mb-3 d-flex gap-4">
                            <div class="flex-fill">
                                <label for="weightRangeFrom">
                                    {{ __('freight_exchange.fields.weight_range_from') }}
                                </label>
                                <golia-input id="weightRangeFrom" type="number"
                                    :additionalAttributes="{ max: 99, min: 1, step: 1 }" :value="weightRangeFrom"
                                    v-model.number="weightRangeFrom"></golia-input>
                            </div>
                            <div class="flex-fill">
                                <label for="weightRangeTo">{{
                                    __('freight_exchange.fields.weight_range_to')
                                    }}</label>
                                <golia-input id="weightRangeTo" type="number"
                                    :additionalAttributes="{ max: 99, min: 1, step: 1 }" :value="weightRangeTo"
                                    v-model.number="weightRangeTo"></golia-input>
                            </div>
                        </div>
                        <div class="mb-3">
                            <label for="dateRange">{{ __('freight_exchange.fields.date_range') }}</label>
                            <golia-date-picker v-model="dateRange" :monthPanels="1" :timePicker="false"
                                :maxRange="1"></golia-date-picker>
                        </div>
                        <div class="destination-filter mb-3">
                            <label>{{ __('freight_exchange.fields.destination') }}</label>
                            <golia-waypoint-search v-model="destinationLocation" />

                            <div v-if="destinationLocation" class="destination-radius mt-3">
                                <div class="destination-radius__header">
                                    <label class="mb-0">
                                        {{ __('freight_exchange.fields.destination_radius') }}
                                    </label>
                                    <strong>{{ destinationRadius }} km</strong>
                                </div>
                                <vue-slider
                                    class="destination-radius__slider"
                                    v-model="destinationRadius"
                                    :min="1"
                                    :max="50"
                                    :interval="1"
                                    :contained="true"
                                    :railStyle="{ backgroundColor: '#ddd' }"
                                    :processStyle="{ backgroundColor: 'var(--dark-blue)' }"
                                />
                            </div>
                        </div>
                        <div class="d-flex gap-2">
                            <golia-button variant="btn-fill success text-smaller" class="w-50"
                                :disabled="!thereIsDifference" :label="__('freight_exchange.fields.apply')"
                                @click="applyFreightExchange" />
                            <golia-button variant="btn-ghost dark-blue text-smaller" class="w-50"
                                style="white-space: nowrap" :label="__('freight_exchange.fields.reset')"
                                @click="resetFilters" />
                        </div>
                        <freight-exchange-control :disabled="!showFreightExchangeControl" :visible="true"
                            :location="FreightExchangeLocation" class="mt-2" :buttonInPanel="true"
                            :formFilters="filtersFromForm" :destination="freightExchangeDestination" />
                    </b-tab>

                    <!-- Tab Carico -->
                    <b-tab :active="getFEOffers.length > 0">
                        <template #title>
                            <div class="position-relative">
                                <div v-if="getFEOffers.length > 0"
                                    class="ge-offers indicator text-white position-absolute translate-middle rounded-pill">
                                    {{ getFEOffers.length }}
                                </div>
                                <span>
                                    <i class="fa-solid fa-briefcase"></i> {{ __('freight_exchange.offers') }}
                                </span>
                            </div>
                        </template>
                        <div v-if="getFEOffers && getFEOffers.length > 0" class="offers-list">
                            <golia-button variant="btn-fill dark-blue-1 text-white text-smaller mb-2" class="w-100"
                                @click="handleClear" icon="fa fa-trash" :label="__('freight_exchange.clear_offers')" />
                            <div v-for="offer in getFEOffers" :key="offer.id" class="offer-card mb-3"
                                @click="focusOnMarker(offer.transportFreight.spots[0])">
                                <!-- Header con nome compagnia -->
                                <div class="offer-header">
                                    <i class="fa-solid fa-building"></i>
                                    <span class="company-name">{{ offer.company_name }}</span>
                                </div>

                                <!-- Route -->
                                <div class="offer-route">
                                    <div class="route-point">
                                        <i class="fa-solid fa-circle-arrow-up text-success"></i>
                                        <span class="locality">{{ offer.transportFreight.spots[0].locality }}</span>
                                    </div>
                                    <div class="route-divider">
                                        <i class="fa-solid fa-arrow-right"></i>
                                    </div>
                                    <div class="route-point">
                                        <i class="fa-solid fa-circle-arrow-down text-danger"></i>
                                        <span class="locality">{{ offer.transportFreight.spots[1].locality }}</span>
                                    </div>
                                </div>

                                <!-- Date e orari -->
                                <div class="offer-dates">
                                    <div class="date-item">
                                        <div class="date-label">
                                            <i class="fa-solid fa-calendar-day"></i>
                                            {{ __('freight_exchange.loading') }}
                                        </div>
                                        <div class="date-value">
                                            <span>

                                                {{ offer.transportFreight.spots[0].local_begin }}
                                                <span class="time-range">
                                                    {{ offer.transportFreight.spots[0].local_hour_begin }}
                                                </span>
                                            </span>
                                            <span>~</span>
                                            <span>
                                                {{ offer.transportFreight.spots[0].local_end }}
                                                <span class="time-range">
                                                    {{ offer.transportFreight.spots[0].local_hour_end }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="date-item">
                                        <div class="date-label">
                                            <i class="fa-solid fa-calendar-check"></i>
                                            {{ __('freight_exchange.unloading') }}
                                        </div>
                                        <div class="date-value">
                                            <span>
                                                {{ offer.transportFreight.spots[1].local_begin }}
                                                <span class="time-range">
                                                    {{ offer.transportFreight.spots[1].local_hour_begin }}
                                                </span>
                                            </span>
                                            <span>~</span>
                                            <span>
                                                {{ offer.transportFreight.spots[1].local_end }}
                                                <span class="time-range">
                                                    {{ offer.transportFreight.spots[1].local_hour_end }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Info veicolo -->
                                <div class="offer-vehicle-info">
                                    <div class="info-badge">
                                        <i class="fa-solid fa-truck"></i>
                                        {{ offer.transportFreight.vehicle_size }}
                                    </div>
                                    <div class="info-badge"
                                        v-if="offer.transportFreight.required_truck_bodies && offer.transportFreight.required_truck_bodies.length > 0">
                                        <i class="fa-solid fa-box-open"></i>
                                        {{ offer.transportFreight.required_truck_bodies.join(', ') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center text-muted py-4">
                            <i class="fa-solid fa-inbox fa-2x mb-2"></i>
                            <p>{{ __('freight_exchange.no_offers') }}</p>
                        </div>
                    </b-tab>
                </b-tabs>
            </div>
        </template>
    </side-panel>
</template>

<script>
    import SidePanel from './SidePanel.vue'
    import { mapGetters, mapActions, mapState } from 'vuex'
    import FreightExchangeControl from '../FreightExchangeControl.vue'

    export default {
        name: 'FreightExchangePanel',
        components: {
            SidePanel,
            FreightExchangeControl
        },
        data() {
            return {
                freightExchangeData: null,
                weightRangeFrom: null,
                weightRangeTo: null,
                dateRange: {
                    start: null,
                    end: null
                },
                vehicleType: [],
                vehicleTypeOptions: [],
                vehicleSize: null,
                vehicleSizeOptions: [],
                staticVehicleSize: [],
                destinationLocation: null,
                destinationRadius: 10
            }
        },
        watch: {
            vehicleType: {
                handler(newVal) {
                    if (newVal.length > 1) {
                        const arrays = newVal.map((n) => n.vehicle_sizes)
                        this.vehicleSizeOptions = arrays.reduce((a, b) =>
                            a.filter((v) => b.some((x) => x.id === v.id))
                        )
                    } else if (newVal.length === 1) {
                        this.vehicleSizeOptions = newVal[0].vehicle_sizes
                    } else {
                        this.vehicleSizeOptions = this.staticVehicleSize
                    }
                    this.vehicleSize = this.vehicleSizeOptions.find(
                        (v) => v.id === this.vehicleSize?.id
                    )
                        ? this.vehicleSize
                        : null
                },
                deep: true
            },
            loadedVehiclesData: {
                handler(newVal) {
                    if (newVal) {
                        this.vehicleType = this.getFEFilters.vehicle_type_ids
                            ? this.vehicleTypeOptions.filter((v) => {
                                return this.getFEFilters.vehicle_type_ids.includes(v.id)
                            })
                            : []

                        this.vehicleSize = this.getFEFilters.vehicle_size_ids?.[0]
                            ? this.staticVehicleSize.find(
                                (v) => v.id === this.getFEFilters.vehicle_size_ids[0]
                            )
                            : null
                    }
                }
            }
        },
        methods: {
            ...mapActions({
                flyToMap: 'flyToMap',
                saveFEFilters: 'saveFEFilters',
                setAndLoadFEOffers: 'setAndLoadFEOffers',
                initializeFiltersFromConfig: 'initializeFiltersFromConfig'
            }),
            async loadVehicleTypes() {
                this.freightExchangeData = JSON.parse(sessionStorage.getItem('freight_exchange'))
                if (
                    this.freightExchangeData == 401 &&
                    new Date(this.freightExchangeData.expires_at) > new Date()
                )
                    return

                await fetch(`${this.$env.GOLIA_EXCHANGE_API}/v1/vehicle-types`, {
                    method: 'GET',
                    headers: {
                        authorization: 'Bearer ' + this.freightExchangeData.token
                    }
                })
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        if (!res.data || res.data.length === 0) return
                        res.data.forEach(element => {
                            element.name = this.__('freight_exchange.vehicle_type.' + element.name.toLowerCase().replaceAll(' ', '_'))
                        });
                        this.vehicleTypeOptions = res.data
                    })
            },
            async loadVehicleSizes() {
                this.freightExchangeData = JSON.parse(sessionStorage.getItem('freight_exchange'))
                if (this.freightExchangeData == 401) return

                await fetch(`${this.$env.GOLIA_EXCHANGE_API}/v1/vehicle-sizes`, {
                    method: 'GET',
                    headers: {
                        authorization: 'Bearer ' + this.freightExchangeData.token
                    }
                })
                    .then((res) => {
                        return res.json()
                    })
                    .then((res) => {
                        res.data.forEach(element => {
                            element.name = this.__('freight_exchange.vehicle_size.' + element.name.toLowerCase().replaceAll(' ', '_'))
                        });
                        this.vehicleSizeOptions = res.data
                        this.staticVehicleSize = res.data
                    })
            },
            async applyFreightExchange() {
                if (this.weightRangeFrom && this.weightRangeTo) {
                    this.weightRangeTo = this.weightRangeTo > 99 ? 99 : this.weightRangeTo
                    if (this.weightRangeFrom > this.weightRangeTo) {
                        this.weightRangeFrom = this.weightRangeTo
                    }
                } else {
                    this.weightRangeFrom = null
                    this.weightRangeTo = null
                }

                // Usa sempre i valori correnti della form
                const filters = this.filtersFromForm

                // Save filters using centralized action
                await this.saveFEFilters({
                    filters: filters,
                    vueInstance: this
                })

                // Close panel
                this.$emit('toggleUtilityPanel')
            },
            resetFilters() {
                this.dateRange = {
                    start: null,
                    end: null
                }
                this.weightRangeFrom = null
                this.weightRangeTo = null
                this.vehicleType = []
                this.vehicleSize = null
                this.destinationLocation = null
                this.destinationRadius = 10
            },
            syncGoliaExchange() {
                window.open('https://exchange.goliaweb.it/', '_blank')
            },
            focusOnMarker(marker) {
                this.$emit('toggleUtilityPanel')
                this.flyToMap({
                    center: [marker.lat, marker.long],
                    zoom: 13
                })
            },
            handleClear() {
                if (this.getFEOffers.length == 0) return
                this.setAndLoadFEOffers([])
            }
        },
        computed: {
            filtersFromForm() {
                // Applica la stessa normalizzazione fatta in `applyFreightExchange`,
                // così anche quando si usa il bottone mappa non serve “salvare” prima.
                let from = this.weightRangeFrom
                let to = this.weightRangeTo

                if (from != null && to != null) {
                    to = to > 99 ? 99 : to
                    if (from > to) from = to
                } else {
                    from = null
                    to = null
                }

                return {
                    vehicle_type_ids: this.vehicleType.map((v) => v.id),
                    vehicle_size_ids: this.vehicleSize ? [this.vehicleSize.id] : null,
                    date_from:
                        this.dateRange.start && this.moment(this.dateRange.start).isValid()
                            ? this.dateRange.start
                            : null,
                    date_to:
                        this.dateRange.end && this.moment(this.dateRange.end).isValid()
                            ? this.dateRange.end
                            : null,
                    from_weight: from,
                    to_weight: to,
                    with_double_trailer: false,
                    with_trailer: false,
                    along_route: false,
                    saved: false,
                    name: 'GeoFleet Freight Exchange',
                    destination_location: this.freightExchangeDestination
                        ? {
                              label: this.freightExchangeDestination.name,
                              referenceCoordinate: {
                                  x: this.freightExchangeDestination.lng,
                                  y: this.freightExchangeDestination.lat
                              }
                          }
                        : null,
                    destination_radius: Number(this.destinationRadius)
                }
            },
            loadedVehiclesData() {
                return this.vehicleTypeOptions.length > 0 && this.staticVehicleSize.length > 0
            },
            ...mapGetters({
                getFEFilters: 'getFEFilters',
                getFEOffers: 'getFEOffers',
                getFEUser: 'getFEUser'
            }),
            thereIsDifference() {
                return (
                    JSON.stringify(this.filtersFromForm) !==
                    sessionStorage.getItem('freight_exchange_filters')
                )
            },
            currentZoom() {
                // Recupera lo zoom corrente dalla mappa principale
                const mainMapInstance = this.$store.state.geoFleet.mainMapInstance
                if (mainMapInstance && mainMapInstance.mapObject) {
                    return mainMapInstance.mapObject.getZoom()
                }
                return 0
            },
            showFreightExchangeControl() {
                return this.currentZoom >= 11 && this.getFEUser !== 401 && this.getFEUser !== null
            },
            freightExchangeDestination() {
                const coordinate = this.destinationLocation?.referenceCoordinate
                const lat = Number(coordinate?.y)
                const lng = Number(coordinate?.x)

                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                if (this.destinationLocation?.placeId && lat === 0 && lng === 0) {
                    return null
                }

                return {
                    lat,
                    lng,
                    radius: Math.max(
                        1,
                        Math.min(50, Number(this.destinationRadius) || 10)
                    ),
                    name: this.destinationLocation.label || `${lat},${lng}`
                }
            },
            FreightExchangeLocation() {
                // Se abbiamo accesso alla mappa principale attraverso lo store
                const mainMapInstance = this.$store.state.geoFleet.mainMapInstance
                if (mainMapInstance && mainMapInstance.mapObject) {
                    const map = mainMapInstance.mapObject
                    const center = map.getCenter()
                    const bounds = map.getBounds()
                    // Calcola il punto più a nord del rettangolo dei bounds (stesso lng del centro)
                    const L = require('leaflet')
                    const north = L.latLng(bounds.getNorth(), center.lng)
                    // Usa la funzione 'distanceTo' di Leaflet per calcolare il raggio in metri
                    const radius = center.distanceTo(north)
                    return {
                        lat: center.lat,
                        lng: center.lng,
                        radius: radius
                    }
                }
                return {
                    lat: null,
                    lng: null,
                    radius: null
                }
            }
        },
        created() {
            this.loadVehicleTypes()
            this.loadVehicleSizes()

            // Initialize filters from config if available (fallback if state initialization didn't work)
            this.initializeFiltersFromConfig()

            if (
                this.getFEFilters.date_from &&
                this.getFEFilters.date_to &&
                this.moment(this.getFEFilters.date_from).isValid() &&
                this.moment(this.getFEFilters.date_to).isValid()
            ) {
                this.dateRange = {
                    start: this.moment(this.getFEFilters.date_from),
                    end: this.moment(this.getFEFilters.date_to)
                }
            }
            this.weightRangeFrom = this.getFEFilters.from_weight ? this.getFEFilters.from_weight : null
            this.weightRangeTo = this.getFEFilters.to_weight ? this.getFEFilters.to_weight : null
            this.destinationLocation = this.getFEFilters.destination_location || null
            this.destinationRadius = Number(this.getFEFilters.destination_radius) || 10
        }
    }
</script>

<style scoped lang="scss">
    @import './SidePanel.scss';

    .ge-offers {
        left: 120%;
        top: -10%;
        background-color: var(--success);
        min-width: 20px;
        text-align: center;
    }

    .destination-filter {
        padding: 14px;
        border: 1px solid var(--bs-gray-300);
        border-radius: 8px;
        background: var(--main-inner-bg-color);
    }

    .destination-radius__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;

        strong {
            color: var(--dark-blue);
            white-space: nowrap;
        }
    }

    .destination-radius__slider {
        margin: 2px 8px 4px;

        &::v-deep(.vue-slider-rail) {
            height: 7px !important;
            border: 1px solid rgba(var(--dark-blue--rgb), 0.25);
            border-radius: 999px;
            background-color: rgba(var(--dark-blue--rgb), 0.18) !important;
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.12);
        }

        &::v-deep(.vue-slider-process) {
            height: 100% !important;
            border-radius: 999px;
            background-color: var(--dark-blue) !important;
        }

        &::v-deep(.vue-slider-dot-handle) {
            border: 2px solid var(--dark-blue);
            background-color: var(--main-bg-color);
            box-shadow: 0 1px 5px rgba(0, 0, 0, 0.28);
        }

        &::v-deep(.vue-slider-dot-handle::after) {
            display: none;
        }
    }

    /* Offers List Styles */
    .offers-list {
        overflow-y: auto;
        padding-right: 4px;
    }

    .offer-card {
        background-color: var(--main-inner-bg-color);
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover {
            background-color: var(--bs-gray-300);
        }
    }

    .offer-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);

        i {
            color: var(--dark-blue);
            font-size: 14px;
        }

        .company-name {
            font-weight: 600;
            font-size: 14px;
            color: var(--main-text-color);
        }
    }

    .offer-route {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 12px;

        .route-point {
            display: flex;
            align-items: center;
            gap: 8px;

            i {
                font-size: 16px;
            }

            .locality {
                font-weight: 500;
                font-size: 13px;
                color: var(--main-text-color);
            }
        }

        .route-divider {
            display: flex;
            justify-content: center;
            color: var(--main-text-color);
            opacity: 0.4;
            margin: 0;

            i {
                font-size: 12px;
            }
        }
    }

    .offer-dates {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 12px;
        padding: 10px;
        background-color: var(--main-bg-color);
        border-radius: 6px;

        .date-item {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .date-label {
                font-size: 11px;
                color: var(--main-text-color);
                opacity: 0.7;
                font-weight: 500;

                i {
                    margin-right: 4px;
                }
            }

            .date-value {
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 12px;
                font-weight: 600;
                color: var(--main-text-color);

                .time-range {
                    margin-left: 8px;
                    font-weight: 400;
                    opacity: 0.8;
                }
            }
        }
    }

    .offer-vehicle-info {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .info-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            background-color: var(--main-bg-color);
            border-radius: 12px;
            font-size: 11px;
            color: var(--main-text-color);
            font-weight: 500;

            i {
                font-size: 10px;
                opacity: 0.7;
            }
        }
    }
</style>
